import { CartLinesUpdateEvent } from '@shopify/events';

async function dispatchCartAdd(detail, lines) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({ action: 'add', context: 'product', lines, promise: deferred.promise })
  );

  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
    const cart = await response.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { ...detail, items: cart.items, itemCount: cart.item_count, didError: false },
    });
  } catch (error) {
    deferred.reject(error);
  }
}

/**
 * <cart-upsell>
 *
 * One stackable cart upsell. The product list (from a collection or from each cart
 * item's product-list metafield) is rendered server-side in cart-upsell.liquid, so it
 * re-evaluates whenever Horizon morphs the cart section after a change. This element
 * only handles the "Add" click via Horizon's native cart-add + section-morph flow.
 */
class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#filterB2BCatalogProducts();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent & { target: HTMLElement }} event */
  #onClick = (event) => {
    const button = event.target.closest('[data-upsell-add]');
    if (!(button instanceof HTMLElement)) return;
    event.preventDefault();
    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;
    this.#add(variantId, button);
  };

  /**
   * @param {string} variantId
   * @param {HTMLElement} button
   */
  async #add(variantId, button) {
    button.setAttribute('disabled', '');
    button.classList.add('is-loading');
    this.#clearError();

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', button.getAttribute('data-quantity') || '1');
    // Liquid omits this display-only property for canonical `bestby-ignore`
    // and legacy `expiring-ignore` products; the product metafield is retained.
    const bestBy = button.getAttribute('data-best-by');
    if (bestBy) body.append('properties[Best By]', bestBy);
    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    const addUrl =
      (typeof window.Theme !== 'undefined' && window.Theme.routes && window.Theme.routes.cart_add_url) ||
      '/cart/add.js';

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json();

      if (data.status) {
        this.#showError(data.description || data.message);
        return;
      }

      await dispatchCartAdd(
        {
          source: 'cart-upsell',
          sourceId: this.id || 'cart-upsell',
          productId: button.getAttribute('data-product-id') || undefined,
          sections: data.sections,
        },
        [{ merchandiseId: variantId, quantity: Number(button.getAttribute('data-quantity') || '1') }]
      );

      // Explicit success feedback: the new line appears at the top of the
      // drawer list (often off-screen on mobile), so without this the tap
      // looks like it did nothing. The section morph usually replaces the
      // strip moments later; this covers the gap and any morph delay.
      const label = button.textContent;
      button.textContent = 'Added ✓';
      button.classList.add('is-added');
      setTimeout(() => {
        if (button.isConnected) {
          button.textContent = label;
          button.classList.remove('is-added');
        }
      }, 2000);
    } catch (error) {
      console.error('[cart-upsell] Failed to add product', error);
      this.#showError();
    } finally {
      button.removeAttribute('disabled');
      button.classList.remove('is-loading');
    }
  }

  /** @param {string} [message] */
  #showError(message) {
    const region = this.querySelector('[data-upsell-error]');
    if (region instanceof HTMLElement) {
      region.textContent = message || 'Sorry, that item could not be added. Please try again.';
      region.hidden = false;
    }
  }

  #clearError() {
    const region = this.querySelector('[data-upsell-error]');
    if (region instanceof HTMLElement) {
      region.textContent = '';
      region.hidden = true;
    }
  }

  async #filterB2BCatalogProducts() {
    if (this.dataset.b2bCatalogFilter !== 'true') return;

    const items = Array.from(this.querySelectorAll('[data-upsell-product-handle]')).filter(
      (item) => item instanceof HTMLElement && item.dataset.b2bCatalogChecked !== 'true'
    );

    if (!items.length) return;

    await Promise.all(
      items.map(async (item) => {
        if (!(item instanceof HTMLElement)) return;

        item.dataset.b2bCatalogPending = 'true';
        const handle = item.dataset.upsellProductHandle;
        const isAccessible = await this.#isProductAccessibleInCurrentCatalog(handle);

        if (isAccessible) {
          item.dataset.b2bCatalogChecked = 'true';
          delete item.dataset.b2bCatalogPending;
        } else {
          item.remove();
        }
      })
    );

    if (!this.querySelector('[data-upsell-product-handle]')) {
      const wrapper = this.closest('.cart-upsell-sidebar-wrapper');
      if (wrapper instanceof HTMLElement) {
        wrapper.remove();
      } else {
        this.remove();
      }
    }
  }

  /** @param {string | undefined} handle */
  async #isProductAccessibleInCurrentCatalog(handle) {
    if (!handle) return false;

    const root = window.Shopify?.routes?.root || '/';
    const productUrl = `${root}products/${encodeURIComponent(handle)}.js`;

    try {
      const response = await fetch(productUrl, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        cache: 'no-store',
      });

      if (response.status === 401 || response.status === 403 || response.status === 404) {
        return false;
      }

      if (!response.ok) return true;

      const product = await response.json();
      if (product.available === false) return false;
      if (Array.isArray(product.variants)) {
        return product.variants.some((variant) => variant.available);
      }

      return true;
    } catch (error) {
      console.warn('[cart-upsell] Could not validate B2B catalog product', handle, error);
      return true;
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
